import { useState } from "react";
import { Accordion } from "../components/utils/Accordion";
import { Typography } from "../components/utils/Typography";

const rights = [
  {
    section: "Section 11",
    title: "Right to Access Information",
    description:
      "See a summary of your personal data being processed, why it's used, and who it's shared with.",
  },
  {
    section: "Section 12",
    title: "Right to Correction & Erasure",
    description:
      "Ask us to correct, complete, or update inaccurate details, or raise a request to erase data.",
  },
  {
    section: "Section 13",
    title: "Right to Grievance Redressal",
    description:
      "Raise a complaint if you're unhappy with how your data or rights are handled.",
  },
  {
    section: "Section 14",
    title: "Right to Nominate",
    description:
      "Choose someone to manage your data rights if something happens to you.",
  },
];

const faqs = [
  {
    question: "Who can see the consent I give here?",
    answer:
      "Only the company (Data Fiduciary) you're consenting to, and NYAI as the Consent Manager facilitating the transaction, can see the consent you give. NYAI's role is limited to maintaining an independent, tamper-evident record of your consent, it doesn't use your data for its own purposes or share it with anyone else. Think of it as a neutral intermediary rather than another party holding your data. This separation is one of the core obligations Consent Managers are held to under the DPDPA Act.",
  },
  {
    question:
      "What is the difference between a Data Fiduciary and a Consent Manager?",
    answer:
      "A Data Fiduciary is the company that collects and uses your personal data for its own purposes, like a bank or an e-commerce platform. A Consent Manager, like NYAI, is a neutral intermediary registered with the Data Protection Board that lets you give, manage, and withdraw consent across all Data Fiduciaries from one place, it doesn't use your data for its own purposes.",
  },
  {
    question: "Can I withdraw consent for only some purposes?",
    answer:
      'Yes. Go to My Consents, open the relevant company, and uncheck the specific purposes you no longer agree to before clicking "Update Preferences". The company will stop processing your data for those purposes while continuing to honor the ones you keep active.',
  },
  {
    question: "Will my data be deleted when I withdraw consent?",
    answer:
      "Withdrawing consent stops the company from processing your data for that purpose going forward, but it doesn't automatically delete data already collected. If you also want your data erased, raise a separate erasure request under Right to Correction & Erasure from the Requests & Complaints section.",
  },
  {
    question: "What can I do if a company doesn't respond to my request?",
    answer:
      "If a Data Fiduciary doesn't respond within the required timeline, you can escalate the request from Requests & Complaints, or file a grievance directly with the Data Protection Board at dpb@gov.in for further action.",
  },
];

export default function RightsFAQs() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className="p-4 md:p-6 w-full">
      <div className="mb-6">
        <Typography variant="sectionTitle" className="md:text-lg">
          Your Rights
        </Typography>
        <Typography variant="body" color="muted" className="mt-1">
          For more information visit the Data Protection Board website here -{" "}
          <a
            href="https://www.dpboard.gov.in"
            target="_blank"
            rel="noopener noreferrer"
            className="text-nyai-teal hover:underline"
          >
            dpboard.gov.in
          </a>
        </Typography>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
        {rights.map((right, idx) => (
          <div
            key={right.section}
            className="rounded-xl border p-4 bg-indigo-50/70 border-indigo-100"
          >
            <div className="text-xs font-medium text-nyai-teal mb-1">
              {right.section}
            </div>
            <Typography variant="sectionTitle" as="h3" className="mb-1">
              {right.title}
            </Typography>
            <Typography variant="body" color="muted" className="leading-relaxed">
              {right.description}
            </Typography>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 mb-6" />

      <div className="mb-4">
        <Typography variant="sectionTitle" id="faq-section">
          Frequently Asked Questions
        </Typography>
        <Typography variant="body" color="muted" className="mt-1">
          Most asked questions related to DPDPA Act, Consent and Data Requests
        </Typography>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {faqs.map((faq, idx) => (
          <Accordion
            key={idx}
            className='bg-indigo-50/70'
            header={
              <Typography variant="bodyStrong" as="span">
                {faq.question}
              </Typography>
            }
            open={openIdx === idx}
            onToggle={() => setOpenIdx((v) => (v === idx ? null : idx))}
          >
            <Typography variant="body" className="leading-relaxed">
              {faq.answer}
            </Typography>
          </Accordion>
        ))}
      </div>
    </div>
  );
}
